import type { AnchorHTMLAttributes } from 'react';

import { cn } from '#/utils/cn';

import type { IconId } from './icons';
import { Icon } from './icons';
import { resolveLinkTarget } from './linkTarget';
import type { HeadingLevel } from './SectionHeader';

export interface SimpleCardProps extends Pick<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'target' | 'rel'
> {
  title: string;
  content?: string | null;
  icon?: IconId;
  href?: string;
  /** Heading element for the card title. Pick for the document outline in descending order (h2 -> h3, etc) */
  headingLevel?: HeadingLevel;
  className?: string;
  /** GTM `data-track` id, forwarded onto the card anchor (link variant only). */
  dataTrack?: string;
  /** GTM `data-track-label`, forwarded onto the card anchor (link variant only). */
  dataTrackLabel?: string;
  /** GTM `data-track-context`, forwarded onto the card anchor (link variant only). */
  dataTrackContext?: string;
}

export function SimpleCard({
  title,
  content,
  href,
  icon = 'newspaper',
  headingLevel: Heading = 'h2',
  className,
  target,
  rel,
  dataTrack,
  dataTrackLabel,
  dataTrackContext,
}: SimpleCardProps) {
  const linkProps = resolveLinkTarget({ href, target, rel });

  const cardClasses = cn(
    'group/article-card flex h-full flex-col',
    href && 'relative',
    className,
  );

  const cardContent = (
    <>
      <div className="flex flex-col items-start gap-12">
        <div className="bg-gray-10 text-blue-60 flex size-40 shrink-0 items-center justify-center rounded-full shadow">
          <Icon id={icon} size={18} aria-hidden="true" />
        </div>

        <div className="flow flow-8">
          {href ? (
            <a
              href={href}
              target={linkProps.target}
              rel={linkProps.rel}
              data-track={dataTrack}
              data-track-label={dataTrackLabel}
              data-track-context={dataTrackContext}
              className="text-fg-strong text-static-14 block leading-tight font-semibold no-underline"
            >
              {title}
              <span
                aria-hidden="true"
                className="after:absolute after:inset-0 after:block after:content-['']"
              />
            </a>
          ) : (
            <Heading className="text-fg-strong text-static-14 leading-tight font-semibold">
              {title}
            </Heading>
          )}

          {content && (
            <p className="text-static-12 text-fg-muted leading-relaxed">
              {content}
            </p>
          )}
        </div>
      </div>
    </>
  );

  return <article className={cardClasses}>{cardContent}</article>;
}
